import { useEffect, useMemo, useState, useRef } from 'react';
import {
  ActionIcon,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Group,
  Loader,
  Modal,
  MultiSelect,
  Progress,
  ScrollArea,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCalendarEvent, IconChartBar, IconCopy, IconExternalLink, IconFileText, IconHeart, IconMessageCircle, IconMusic, IconPaperclip, IconPhoto, IconPlus, IconPrinter, IconSend, IconSpeakerphone, IconTrash, IconX } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchPublicProfiles } from '../lib/publicProfiles';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../lib/auth/useAuth';
import { PageHeader } from '../components/PageHeader';
import { EmptyState } from '../components/EmptyState';

type NamedRow = { id: string; name: string };

type FeedRow = {
  id: string;
  user_id: string;
  text: string;
  image_url: string | null;
  created_at: string;
  author_first_name?: string | null;
  author_last_name?: string | null;
  author_nickname?: string | null;
  comment_count?: number | null;
  like_count?: number | null;
  love_count?: number | null;
  fire_count?: number | null;
  visible_ressorts?: string[] | null;
  visible_roles?: string[] | null;
  poll?: FeedPoll | null;
};

type FeedPollOption = {
  id: string;
  text: string;
  position: number;
  vote_count: number;
  is_user_generated?: boolean;
  voter_names?: string[];
};

type FeedPoll = {
  id: string;
  question: string;
  allow_multiple: boolean;
  allow_user_options?: boolean;
  menu_url?: string | null;
  event_id?: string | null;
  event_title?: string | null;
  event_type?: string | null;
  event_start_time?: string | null;
  event_end_time?: string | null;
  ends_at?: string | null;
  is_closed?: boolean;
  total_votes: number;
  my_option_ids?: string[];
  options: FeedPollOption[];
};

type EventRow = {
  id: string;
  title: string;
  event_type: string | null;
  start_time: string;
  end_time: string | null;
};

type PollEvaluationParticipant = {
  user_id: string;
  display_name: string;
  avatar_url: string | null;
  selected_option_ids: string[];
  selected_option_texts: string[];
  voted_at: string | null;
};

type PollEvaluation = {
  poll: FeedPoll;
  participantCount: number;
  eligibleVoterCount: number;
  participants: PollEvaluationParticipant[];
};

type CommentRow = {
  id: string;
  post_id: string | null;
  user_id: string | null;
  comment_text: string;
  created_at: string | null;
};

type CommentLikeRow = {
  id: string;
  post_id: string;
  comment_id: string;
  user_id: string;
  created_at: string;
};

type PublicProfileRow = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  nickname: string | null;
  avatar_url: string | null;
};

type FeedAttachmentRow = {
  id: string;
  feed_id: string;
  user_id: string;
  type: string;
  bucket_id: string;
  object_path: string;
  file_name: string | null;
  mime_type: string | null;
  size_bytes: number | null;
  created_at: string;
};

function parseImageUrls(imageUrl: string | null): string[] {
  if (!imageUrl) return [];
  return imageUrl
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);
}

function authorLabel(p: FeedRow) {
  const nn = (p.author_nickname ?? '').trim();
  const fn = (p.author_first_name ?? '').trim();
  const ln = (p.author_last_name ?? '').trim();
  if (nn) return nn;
  const base = [fn, ln].filter(Boolean).join(' ');
  return base || 'Unbekannt';
}

function profileLabel(p: PublicProfileRow | undefined) {
  if (!p) return 'Unbekannt';
  const nn = (p.nickname ?? '').trim();
  const fn = (p.first_name ?? '').trim();
  const ln = (p.last_name ?? '').trim();
  if (nn) return nn;
  const base = [fn, ln].filter(Boolean).join(' ');
  return base || 'Unbekannt';
}

function formatBytes(bytes: number | null | undefined): string {
  if (!bytes || bytes <= 0) return '';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let b = bytes;
  let i = 0;
  while (b >= 1024 && i < units.length - 1) {
    b /= 1024;
    i += 1;
  }
  const digits = i === 0 ? 0 : i === 1 ? 0 : 1;
  return `${b.toFixed(digits)} ${units[i]}`;
}

function normalizePoll(raw: unknown): FeedPoll | null {
  if (!raw || typeof raw !== 'object') return null;
  const map = raw as Record<string, unknown>;
  const id = String(map.id ?? '').trim();
  const question = String(map.question ?? '').trim();
  if (!id || !question) return null;

  const rawOptions = Array.isArray(map.options) ? map.options : [];
  const options: FeedPollOption[] = rawOptions
    .map((entry): FeedPollOption | null => {
      if (!entry || typeof entry !== 'object') return null;
      const e = entry as Record<string, unknown>;
      const optionId = String(e.id ?? '').trim();
      const text = String(e.text ?? e.option_text ?? '').trim();
      if (!optionId || !text) return null;
      return {
        id: optionId,
        text,
        position: Number(e.position ?? 0) || 0,
        vote_count: Number(e.vote_count ?? 0) || 0,
        is_user_generated: e.is_user_generated === true,
        voter_names: Array.isArray(e.voter_names) ? e.voter_names.map((x) => String(x)).filter(Boolean) : [],
      };
    })
    .filter((v): v is FeedPollOption => v !== null)
    .sort((a, b) => a.position - b.position);

  const my = Array.isArray(map.my_option_ids)
    ? map.my_option_ids.map((x) => String(x)).filter(Boolean)
    : [];

  return {
    id,
    question,
    allow_multiple: map.allow_multiple === true,
    allow_user_options: map.allow_user_options === true,
    menu_url: map.menu_url == null ? null : String(map.menu_url),
    event_id: map.event_id == null ? null : String(map.event_id),
    event_title: map.event_title == null ? null : String(map.event_title),
    event_type: map.event_type == null ? null : String(map.event_type),
    event_start_time: map.event_start_time == null ? null : String(map.event_start_time),
    event_end_time: map.event_end_time == null ? null : String(map.event_end_time),
    ends_at: map.ends_at == null ? null : String(map.ends_at),
    is_closed: map.is_closed === true || isPollClosed(map.ends_at == null ? null : String(map.ends_at)),
    total_votes: Number(map.total_votes ?? 0) || 0,
    my_option_ids: my,
    options,
  };
}

function normalizeFeedRows(rows: unknown[]): FeedRow[] {
  return rows.map((row) => {
    const r = row as FeedRow & { poll?: unknown };
    return {
      ...r,
      poll: normalizePoll(r.poll),
    };
  });
}

function sanitizePollOptions(values: string[]): string[] {
  const dedupe = new Set<string>();
  const out: string[] = [];
  for (const raw of values) {
    const v = raw.trim();
    if (!v) continue;
    const key = v.toLowerCase();
    if (dedupe.has(key)) continue;
    dedupe.add(key);
    out.push(v);
  }
  return out;
}

function asStringArray(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((x) => String(x).trim()).filter(Boolean);
}

function formatEventSummary(
  title: string | null | undefined,
  eventType: string | null | undefined,
  startTime: string | null | undefined,
  endTime: string | null | undefined,
): string {
  const parts: string[] = [];
  const cleanTitle = (title ?? '').trim();
  const cleanType = (eventType ?? '').trim();
  if (cleanTitle) parts.push(cleanTitle);
  if (cleanType) parts.push(cleanType);

  if (startTime) {
    const start = new Date(startTime);
    if (!Number.isNaN(start.getTime())) {
      const day = start.toLocaleDateString('de-DE');
      const startClock = start.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
      let datePart = `${day} ${startClock}`;
      if (endTime) {
        const end = new Date(endTime);
        if (!Number.isNaN(end.getTime()) && end.getTime() >= start.getTime()) {
          const endClock = end.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
          datePart = `${day} ${startClock}-${endClock}`;
        }
      }
      parts.push(datePart);
    }
  }

  return parts.join(' · ');
}

function formatPollDeadlineLabel(raw: string | null | undefined): string {
  if (!raw) return '';
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function toDateTimeLocalValue(raw: string | null | undefined): string {
  if (!raw) return '';
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  const hours = `${date.getHours()}`.padStart(2, '0');
  const minutes = `${date.getMinutes()}`.padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function isPollClosed(
  endsAt: string | null | undefined,
  explicitClosed?: boolean,
): boolean {
  if (explicitClosed === true) return true;
  if (!endsAt) return false;
  const date = new Date(endsAt);
  if (Number.isNaN(date.getTime())) return false;
  return date.getTime() <= Date.now();
}

function formatPercent(value: number): string {
  const bounded = Math.min(100, Math.max(0, value));
  const hasFraction = Math.abs(bounded - Math.round(bounded)) >= 0.05;
  return `${bounded.toFixed(hasFraction ? 1 : 0)}%`;
}

function normalizePollEvaluation(raw: unknown): PollEvaluation | null {
  if (!raw || typeof raw !== 'object') return null;
  const payload = raw as Record<string, unknown>;
  const pollRaw = payload.poll;
  const poll = normalizePoll(pollRaw);
  if (!poll) return null;

  const pollMap = pollRaw && typeof pollRaw === 'object' ? (pollRaw as Record<string, unknown>) : {};
  const participantsRaw = Array.isArray(payload.participants) ? payload.participants : [];
  const participants: PollEvaluationParticipant[] = participantsRaw
    .map((entry): PollEvaluationParticipant | null => {
      if (!entry || typeof entry !== 'object') return null;
      const map = entry as Record<string, unknown>;
      const userId = String(map.user_id ?? '').trim();
      if (!userId) return null;
      return {
        user_id: userId,
        display_name: String(map.display_name ?? 'Unbekannt').trim() || 'Unbekannt',
        avatar_url: map.avatar_url == null ? null : String(map.avatar_url),
        selected_option_ids: asStringArray(map.selected_option_ids),
        selected_option_texts: asStringArray(map.selected_option_texts),
        voted_at: map.voted_at == null ? null : String(map.voted_at),
      };
    })
    .filter((v): v is PollEvaluationParticipant => v !== null);
  participants.sort((a, b) => a.display_name.toLowerCase().localeCompare(b.display_name.toLowerCase()));

  const participantCount = Number(pollMap.participant_count ?? participants.length) || participants.length;
  const eligibleVoterCount = Number(pollMap.eligible_voter_count ?? 0) || 0;

  return {
    poll: {
      ...poll,
      total_votes: Number(pollMap.total_votes ?? poll.total_votes) || poll.total_votes,
    },
    participantCount,
    eligibleVoterCount,
    participants,
  };
}

type PollOrderRow = {
  optionId: string;
  optionText: string;
  count: number;
  voters: string[];
};

function buildPollOrderRows(evaluation: PollEvaluation): PollOrderRow[] {
  const rows: PollOrderRow[] = [];

  for (const option of evaluation.poll.options) {
    const voters = evaluation.participants
      .filter((entry) => entry.selected_option_ids.includes(option.id))
      .map((entry) => entry.display_name)
      .filter((name) => name.trim().length > 0)
      .sort((a, b) => a.localeCompare(b, 'de', { sensitivity: 'base' }));

    rows.push({
      optionId: option.id,
      optionText: option.text,
      count: voters.length,
      voters,
    });
  }

  return rows.sort((a, b) => {
    if (a.count !== b.count) return b.count - a.count;
    return a.optionText.localeCompare(b.optionText, 'de', { sensitivity: 'base' });
  });
}

function buildPollOrderSummaryText(evaluation: PollEvaluation): string {
  const rows = buildPollOrderRows(evaluation);
  const out: string[] = ['Umfrage-Auswertung', evaluation.poll.question, ''];
  if (evaluation.eligibleVoterCount > 0) {
    const ratio = (evaluation.participantCount / evaluation.eligibleVoterCount) * 100;
    out.push(
      `Teilnahme: ${evaluation.participantCount} von ${evaluation.eligibleVoterCount} (${formatPercent(ratio)})`,
    );
    out.push('');
  }
  const eventSummary = formatEventSummary(
    evaluation.poll.event_title,
    evaluation.poll.event_type,
    evaluation.poll.event_start_time,
    evaluation.poll.event_end_time,
  );
  if (eventSummary) {
    out.push(`Termin: ${eventSummary}`);
    out.push('');
  }

  for (const row of rows) {
    out.push(`${row.optionText} (${row.count})`);
    if (row.voters.length === 0) {
      out.push('- niemand');
    } else {
      for (const voter of row.voters) {
        out.push(`- ${voter}`);
      }
    }
    out.push('');
  }

  return out.join('\n').trimEnd();
}

function exportPollCsv(evaluation: PollEvaluation) {
  const header = 'Name,Optionen,Zeitstempel';
  const rows = [...evaluation.participants]
    .sort((a, b) => a.display_name.localeCompare(b.display_name, 'de', { sensitivity: 'base' }))
    .map((p) => {
      const name = `"${p.display_name.replace(/"/g, '""')}"`;
      const options = `"${p.selected_option_texts.join(' | ').replace(/"/g, '""')}"`;
      const ts = p.voted_at ? `"${new Date(p.voted_at).toLocaleString()}"` : '""';
      return [name, options, ts].join(',');
    });
  const csv = [header, ...rows].join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `umfrage-${evaluation.poll.id.slice(0, 8)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function NewsPage() {
  const qc = useQueryClient();
  const { session, capabilities } = useAuth();

  useEffect(() => {
    document.title = 'News – Musical!Kultur Admin';
  }, []);

  const [text, setText] = useState('');
  const [composeType, setComposeType] = useState<'post' | 'poll'>('post');
  const [sendToAll, setSendToAll] = useState(true);
  const [roleNames, setRoleNames] = useState<string[]>([]);
  const [ressortNames, setRessortNames] = useState<string[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [pollQuestion, setPollQuestion] = useState('');
  const [pollAllowMultiple, setPollAllowMultiple] = useState(false);
  const [pollAllowUserOptions, setPollAllowUserOptions] = useState(false);
  const [pollMenuUrl, setPollMenuUrl] = useState('');
  const [pollEventId, setPollEventId] = useState<string | null>(null);
  const [pollHasDeadline, setPollHasDeadline] = useState(false);
  const [pollEndsAt, setPollEndsAt] = useState('');
  const [pollOptions, setPollOptions] = useState<string[]>(['', '']);

  const [commentsOpen, setCommentsOpen] = useState(false);
  const [commentsPost, setCommentsPost] = useState<FeedRow | null>(null);
  const [commentSearch, setCommentSearch] = useState('');
  const [commentDraft, setCommentDraft] = useState('');

  const [attachmentsOpen, setAttachmentsOpen] = useState(false);
  const [attachmentsPost, setAttachmentsPost] = useState<FeedRow | null>(null);
  const [pollEvalOpen, setPollEvalOpen] = useState(false);
  const [pollEvalPost, setPollEvalPost] = useState<FeedRow | null>(null);
  const [extendDeadlineValue, setExtendDeadlineValue] = useState('');

  const [filterRoles, setFilterRoles] = useState<string[]>([]);
  const [filterRessorts, setFilterRessorts] = useState<string[]>([]);
  const [showCount, setShowCount] = useState(40);

  const rolesQ = useQuery({
    queryKey: ['roles'],
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<NamedRow[]> => {
      const { data, error } = await supabase.from('roles').select('id,name').order('name');
      if (error) throw error;
      return (data ?? []) as NamedRow[];
    },
  });

  const ressortsQ = useQuery({
    queryKey: ['ressorts'],
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<NamedRow[]> => {
      const { data, error } = await supabase.from('ressorts').select('id,name').order('name');
      if (error) throw error;
      return (data ?? []) as NamedRow[];
    },
  });

  const eventsQ = useQuery({
    queryKey: ['events-for-polls'],
    enabled: !!session?.user?.id,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<EventRow[]> => {
      const from = new Date(Date.now() - 1000 * 60 * 60 * 24 * 30).toISOString();
      const { data, error } = await supabase
        .from('events')
        .select('id,title,event_type,start_time,end_time')
        .gte('start_time', from)
        .order('start_time', { ascending: true })
        .limit(500);
      if (error) throw error;
      return (data ?? []) as EventRow[];
    },
  });

  const feedQ = useQuery({
    queryKey: ['feed', 'recent'],
    enabled: !!session?.user?.id,
    staleTime: 60_000,
    queryFn: async (): Promise<FeedRow[]> => {
      const { data, error } = await supabase.rpc('get_my_feeds_with_polls');
      if (error) throw error;
      return normalizeFeedRows((data ?? []) as unknown[]);
    },
  });

  const filteredFeed = useMemo(() => {
    const items = feedQ.data ?? [];
    const wantRoles = new Set(filterRoles.map((x) => x.trim()).filter(Boolean));
    const wantRessorts = new Set(filterRessorts.map((x) => x.trim()).filter(Boolean));
    const hasFilters = wantRoles.size > 0 || wantRessorts.size > 0;

    const matches = (p: FeedRow) => {
      const roles = (p.visible_roles ?? []).map((x) => String(x));
      const ressorts = (p.visible_ressorts ?? []).map((x) => String(x));
      const isGlobal = roles.length === 0 && ressorts.length === 0;

      if (!hasFilters) return true;
      if (isGlobal) return true; // global posts are visible to everyone -> include in "what X sees" view

      for (const r of roles) if (wantRoles.has(r)) return true;
      for (const r of ressorts) if (wantRessorts.has(r)) return true;
      return false;
    };

    return items.filter(matches);
  }, [feedQ.data, filterRoles, filterRessorts]);

  const shownFeed = useMemo(() => filteredFeed.slice(0, Math.max(1, showCount)), [filteredFeed, showCount]);
  const hasMoreFeed = shownFeed.length < filteredFeed.length;
  const shownFeedIds = useMemo(() => shownFeed.map((p) => p.id), [shownFeed]);

  const recentAttachmentsQ = useQuery({
    queryKey: ['feed-attachments', 'recent', shownFeedIds.join(',')],
    enabled: shownFeedIds.length > 0,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<FeedAttachmentRow[]> => {
      const { data, error } = await supabase
        .from('feed_attachments')
        .select('id,feed_id,user_id,type,bucket_id,object_path,file_name,mime_type,size_bytes,created_at')
        .in('feed_id', shownFeedIds);
      if (error) throw error;
      return (data ?? []) as FeedAttachmentRow[];
    },
  });

  const postAttachmentsQ = useQuery({
    queryKey: ['feed-attachments', attachmentsPost?.id ?? 'none'],
    enabled: attachmentsOpen && !!attachmentsPost?.id,
    queryFn: async (): Promise<FeedAttachmentRow[]> => {
      const pid = attachmentsPost!.id;
      const { data, error } = await supabase
        .from('feed_attachments')
        .select('id,feed_id,user_id,type,bucket_id,object_path,file_name,mime_type,size_bytes,created_at')
        .eq('feed_id', pid)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as FeedAttachmentRow[];
    },
  });

  const pollEvalQ = useQuery({
    queryKey: ['poll-evaluation', pollEvalPost?.id ?? 'none'],
    enabled: pollEvalOpen && !!pollEvalPost?.id,
    queryFn: async (): Promise<PollEvaluation | null> => {
      let pollId = (pollEvalPost?.poll?.id ?? '').trim();
      if (!pollId) {
        const { data: pollRow, error: pollErr } = await supabase
          .from('feed_polls')
          .select('id')
          .eq('feed_id', pollEvalPost!.id)
          .maybeSingle();
        if (pollErr) throw pollErr;
        if (!pollRow) return null;
        pollId = String(pollRow.id ?? '').trim();
      }
      if (!pollId) return null;

      const { data, error } = await supabase.rpc('get_feed_poll_evaluation', { p_poll_id: pollId });
      if (error) throw error;
      return normalizePollEvaluation(data);
    },
  });

  const commentsQ = useQuery({
    queryKey: ['comments', commentsPost?.id ?? 'none'],
    enabled: commentsOpen && !!commentsPost?.id,
    staleTime: 30_000,
    queryFn: async (): Promise<CommentRow[]> => {
      const pid = commentsPost!.id;
      const { data, error } = await supabase
        .from('comments')
        .select('id,post_id,user_id,comment_text,created_at')
        .eq('post_id', pid)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as CommentRow[];
    },
  });

  const commentLikesQ = useQuery({
    queryKey: ['comment-likes', commentsPost?.id ?? 'none'],
    enabled: commentsOpen && !!commentsPost?.id,
    staleTime: 30_000,
    queryFn: async (): Promise<CommentLikeRow[]> => {
      const pid = commentsPost!.id;
      const { data, error } = await supabase
        .from('comment_likes')
        .select('id,post_id,comment_id,user_id,created_at')
        .eq('post_id', pid);
      if (error) throw error;
      return (data ?? []) as CommentLikeRow[];
    },
  });

  const commentAuthorIds = useMemo(() => {
    const ids = new Set<string>();
    for (const c of commentsQ.data ?? []) {
      const uid = (c.user_id ?? '').trim();
      if (uid) ids.add(uid);
    }
    return Array.from(ids);
  }, [commentsQ.data]);

  const profilesQ = useQuery({
    queryKey: ['public-profiles', commentsPost?.id ?? 'none', commentAuthorIds.join(',')],
    enabled: commentsOpen && commentAuthorIds.length > 0,
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<PublicProfileRow[]> => {
      return (await fetchPublicProfiles(commentAuthorIds)).map((row) => ({
        id: row.id,
        first_name: row.first_name,
        last_name: row.last_name,
        nickname: row.nickname,
        avatar_url: row.avatar_url,
      }));
    },
  });

  useEffect(() => {
    if (!session?.user?.id) return;

    const ch = supabase
      .channel(`admin-feed-live:${session.user.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'feed' },
        async () => {
          await qc.invalidateQueries({ queryKey: ['feed', 'recent'] });
        },
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'feed_reactions' },
        async () => {
          await qc.invalidateQueries({ queryKey: ['feed', 'recent'] });
        },
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'comments' },
        async () => {
          await qc.invalidateQueries({ queryKey: ['feed', 'recent'] });
        },
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'feed_attachments' },
        async () => {
          await qc.invalidateQueries({ queryKey: ['feed-attachments'] });
        },
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'feed_polls' },
        async () => {
          await qc.invalidateQueries({ queryKey: ['feed', 'recent'] });
        },
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'feed_poll_votes' },
        async () => {
          await qc.invalidateQueries({ queryKey: ['feed', 'recent'] });
        },
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'feed_poll_options' },
        async () => {
          await qc.invalidateQueries({ queryKey: ['feed', 'recent'] });
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(ch);
    };
  }, [qc, session?.user?.id]);

  useEffect(() => {
    if (!commentsOpen || !commentsPost?.id) return;
    const pid = commentsPost.id;

    const ch = supabase
      .channel(`admin-comments:${pid}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'comments', filter: `post_id=eq.${pid}` },
        async () => {
          await qc.invalidateQueries({ queryKey: ['comments', pid] });
          await qc.invalidateQueries({ queryKey: ['feed'] });
        },
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'comment_likes', filter: `post_id=eq.${pid}` },
        async () => {
          await qc.invalidateQueries({ queryKey: ['comment-likes', pid] });
          await qc.invalidateQueries({ queryKey: ['feed'] });
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(ch);
    };
  }, [commentsOpen, commentsPost?.id, qc]);

  useEffect(() => {
    if (!attachmentsOpen || !attachmentsPost?.id) return;
    const pid = attachmentsPost.id;

    const ch = supabase
      .channel(`admin-attachments:${pid}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'feed_attachments', filter: `feed_id=eq.${pid}` },
        async () => {
          await qc.invalidateQueries({ queryKey: ['feed-attachments'] });
          await qc.invalidateQueries({ queryKey: ['feed'] });
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(ch);
    };
  }, [attachmentsOpen, attachmentsPost?.id, qc]);

  useEffect(() => {
    if (!pollEvalOpen) return;
    const pollId = pollEvalQ.data?.poll.id ?? pollEvalPost?.poll?.id;
    if (!pollId) return;

    const ch = supabase
      .channel(`admin-poll-eval:${pollId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'feed_poll_votes', filter: `poll_id=eq.${pollId}` },
        async () => {
          await qc.invalidateQueries({ queryKey: ['poll-evaluation', pollEvalPost?.id ?? 'none'] });
          await qc.invalidateQueries({ queryKey: ['feed'] });
        },
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'feed_poll_options', filter: `poll_id=eq.${pollId}` },
        async () => {
          await qc.invalidateQueries({ queryKey: ['poll-evaluation', pollEvalPost?.id ?? 'none'] });
          await qc.invalidateQueries({ queryKey: ['feed'] });
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(ch);
    };
  }, [pollEvalOpen, pollEvalPost?.id, pollEvalPost?.poll?.id, pollEvalQ.data?.poll.id, qc]);

  useEffect(() => {
    if (!pollEvalOpen || !pollEvalQ.data) {
      setExtendDeadlineValue('');
      return;
    }
    const current = toDateTimeLocalValue(pollEvalQ.data.poll.ends_at);
    if (current) {
      setExtendDeadlineValue(current);
      return;
    }
    const fallback = toDateTimeLocalValue(new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString());
    setExtendDeadlineValue(fallback);
  }, [pollEvalOpen, pollEvalQ.data, pollEvalQ.data?.poll.id, pollEvalQ.data?.poll.ends_at]);

  const profileById = useMemo(() => {
    const m = new Map<string, PublicProfileRow>();
    for (const p of profilesQ.data ?? []) m.set(p.id, p);
    return m;
  }, [profilesQ.data]);

  const likeInfo = useMemo(() => {
    const counts = new Map<string, number>();
    const mine = new Set<string>();
    const myId = session?.user?.id ?? '';
    for (const l of commentLikesQ.data ?? []) {
      counts.set(l.comment_id, (counts.get(l.comment_id) ?? 0) + 1);
      if (myId && l.user_id === myId) mine.add(l.comment_id);
    }
    return { counts, mine };
  }, [commentLikesQ.data, session?.user?.id]);

  // Long-press (hold) to view who liked a comment without toggling the like
  const [likesModalOpen, setLikesModalOpen] = useState(false);
  const [likesModalProfiles, setLikesModalProfiles] = useState<PublicProfileRow[]>([]);
  const [likesModalLoading, setLikesModalLoading] = useState(false);

  const longPressTimerRef = useRef<number | null>(null);
  const longPressTriggeredRef = useRef(false);
  const LONG_PRESS_MS = 600;

  async function openLikesModalFor(commentId: string) {
    setLikesModalLoading(true);
    try {
      const likes = (commentLikesQ.data ?? []).filter((l) => l.comment_id === commentId);
      const ids = Array.from(new Set(likes.map((l) => l.user_id))).filter(Boolean);
      if (ids.length === 0) {
        setLikesModalProfiles([]);
        setLikesModalOpen(true);
        return;
      }
      const data = await fetchPublicProfiles(ids);
      setLikesModalProfiles(data.map((row) => ({
        id: row.id,
        first_name: row.first_name,
        last_name: row.last_name,
        nickname: row.nickname,
        avatar_url: row.avatar_url,
      })));
      setLikesModalOpen(true);
    } catch (e) {
      notifications.show({ color: 'red', title: 'Fehler', message: 'Konnte Likeliste nicht laden.' });
    } finally {
      setLikesModalLoading(false);
    }
  }

  function startLongPress(commentId: string) {
    longPressTriggeredRef.current = false;
    if (longPressTimerRef.current !== null) window.clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = window.setTimeout(() => {
      longPressTriggeredRef.current = true;
      void openLikesModalFor(commentId);
    }, LONG_PRESS_MS);
  }

  function cancelLongPress() {
    if (longPressTimerRef.current !== null) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }

  function handleLikeRelease(commentId: string) {
    if (longPressTimerRef.current !== null) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    if (!longPressTriggeredRef.current) {
      toggleLikeM.mutate(commentId);
    }
    longPressTriggeredRef.current = false;
  }

  const pollOrderRows = useMemo(() => {
    if (!pollEvalQ.data) return [] as PollOrderRow[];
    return buildPollOrderRows(pollEvalQ.data);
  }, [pollEvalQ.data]);

  const myPollSelectionText = useMemo(() => {
    const myId = session?.user?.id ?? '';
    if (!myId || !pollEvalQ.data) return '';
    const me = pollEvalQ.data.participants.find((entry) => entry.user_id === myId);
    if (!me || me.selected_option_texts.length === 0) return '';
    return me.selected_option_texts.join(', ');
  }, [pollEvalQ.data, session?.user?.id]);

  const pollEvalEventSummary = useMemo(() => {
    if (!pollEvalQ.data) return '';
    return formatEventSummary(
      pollEvalQ.data.poll.event_title,
      pollEvalQ.data.poll.event_type,
      pollEvalQ.data.poll.event_start_time,
      pollEvalQ.data.poll.event_end_time,
    );
  }, [pollEvalQ.data]);

  const pollParticipationSummary = useMemo(() => {
    if (!pollEvalQ.data) return '';
    const eligible = pollEvalQ.data.eligibleVoterCount > 0
      ? pollEvalQ.data.eligibleVoterCount
      : pollEvalQ.data.participantCount;
    if (eligible <= 0) return '';
    const ratio = (pollEvalQ.data.participantCount / eligible) * 100;
    return `${pollEvalQ.data.participantCount}/${eligible} (${formatPercent(ratio)})`;
  }, [pollEvalQ.data]);

  async function copyPollOrderSummary() {
    if (!pollEvalQ.data) return;
    try {
      const payload = buildPollOrderSummaryText(pollEvalQ.data);
      await navigator.clipboard.writeText(payload);
      notifications.show({
        color: 'teal',
        title: 'Kopiert',
        message: 'Umfrage-Ergebnisse wurden in die Zwischenablage kopiert.',
      });
    } catch {
      notifications.show({
        color: 'red',
        title: 'Fehler',
        message: 'Konnte die Umfrage-Ergebnisse nicht kopieren.',
      });
    }
  }

  const roleOptions = useMemo(
    () => (rolesQ.data ?? []).map((r) => ({ value: r.name, label: r.name })),
    [rolesQ.data],
  );
  const ressortOptions = useMemo(
    () => (ressortsQ.data ?? []).map((r) => ({ value: r.name, label: r.name })),
    [ressortsQ.data],
  );
  const pollEventOptions = useMemo(
    () => {
      const grouped = new Map<string, Array<{ value: string; label: string }>>();
      for (const event of eventsQ.data ?? []) {
        const start = new Date(event.start_time);
        const dayLabel = Number.isNaN(start.getTime())
          ? 'Ohne Datum'
          : start.toLocaleDateString('de-DE', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            });
        const timeLabel = Number.isNaN(start.getTime())
          ? '--:--'
          : start.toLocaleTimeString('de-DE', {
              hour: '2-digit',
              minute: '2-digit',
            });
        const title = (event.title ?? '').trim() || 'Termin';
        const type = (event.event_type ?? '').trim();
        const label = `${timeLabel} : ${title}${type ? ` · ${type}` : ''}`;
        if (!grouped.has(dayLabel)) grouped.set(dayLabel, []);
        grouped.get(dayLabel)!.push({ value: event.id, label });
      }

      return Array.from(grouped.entries()).map(([group, items]) => ({
        group,
        items,
      }));
    },
    [eventsQ.data],
  );

  function renderVisibilityBadges(p: FeedRow) {
    const roles = (p.visible_roles ?? []).filter((x) => String(x ?? '').trim().length > 0);
    const ressorts = (p.visible_ressorts ?? []).filter((x) => String(x ?? '').trim().length > 0);
    const isGlobal = roles.length === 0 && ressorts.length === 0;

    const max = 6;
    const all = [
      ...roles.map((x) => ({ kind: 'role' as const, label: String(x) })),
      ...ressorts.map((x) => ({ kind: 'ressort' as const, label: String(x) })),
    ];

    const show = all.slice(0, max);
    const more = Math.max(0, all.length - show.length);

    return (
      <Group gap={6} mt={6}>
        {isGlobal && (
          <Badge className="contrast-badge-neutral" variant="light" color="gray">
            Alle
          </Badge>
        )}
        {show.map((x) => (
          <Badge key={`${p.id}-${x.kind}-${x.label}`} variant="light" color={x.kind === 'role' ? 'brand' : 'grape'}>
            {x.label}
          </Badge>
        ))}
        {more > 0 && (
          <Badge className="contrast-badge-neutral" variant="light" color="gray">
            +{more}
          </Badge>
        )}
      </Group>
    );
  }

  const createM = useMutation({
    mutationFn: async () => {
      const body = text.trim();
      if (composeType === 'post' && !body && files.length === 0 && attachments.length === 0) {
        throw new Error('Bitte Text eingeben oder mindestens ein Bild/Datei hinzufügen.');
      }
      if (!sendToAll && roleNames.length === 0 && ressortNames.length === 0) {
        throw new Error('Bitte mindestens eine Rolle oder ein Ressort auswählen (oder "An alle").');
      }
      if (composeType === 'poll' && !pollQuestion.trim()) {
        throw new Error('Bitte eine Umfragefrage eingeben.');
      }
      const normalizedOptions = sanitizePollOptions(pollOptions);
      if (composeType === 'poll' && !pollAllowUserOptions && normalizedOptions.length < 2) {
        throw new Error('Bitte mindestens zwei unterschiedliche Umfrageoptionen eingeben.');
      }
      if (composeType === 'poll' && normalizedOptions.length > 10) {
        throw new Error('Maximal 10 Umfrageoptionen sind erlaubt.');
      }
      let pollEndsAtIso: string | null = null;
      if (composeType === 'poll' && pollHasDeadline) {
        if (!pollEndsAt.trim()) {
          throw new Error('Bitte eine Endzeit für die Umfrage auswählen.');
        }
        const parsedEnd = new Date(pollEndsAt);
        if (Number.isNaN(parsedEnd.getTime())) {
          throw new Error('Ungültige Endzeit für die Umfrage.');
        }
        if (parsedEnd.getTime() <= Date.now() + 60 * 1000) {
          throw new Error('Die Endzeit muss in der Zukunft liegen.');
        }
        pollEndsAtIso = parsedEnd.toISOString();
      }

      // 1) Bilder hochladen (optional)
      const urls: string[] = [];
      for (const f of files) {
        const ext = (f.name.split('.').pop() ?? 'jpg').toLowerCase();
        const safeExt = ext.match(/^[a-z0-9]+$/) ? ext : 'jpg';
        const id = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : String(Date.now());
        const path = `feed/${Date.now()}_${id}.${safeExt}`;

        const { error: upErr } = await supabase.storage.from('FeedPictures').upload(path, f, {
          upsert: false,
          contentType: f.type || undefined,
          cacheControl: '3600',
        });
        if (upErr) throw upErr;

        const pub = supabase.storage.from('FeedPictures').getPublicUrl(path).data.publicUrl;
        urls.push(pub);
      }

      // 2) Feed oder Poll erstellen
      let feedId = '';
      if (composeType === 'poll') {
        const { data, error } = await supabase.rpc('create_feed_poll', {
          p_text: body,
          p_image_urls: urls.join(','),
          p_question: pollQuestion.trim(),
          p_option_texts: normalizedOptions,
          p_allow_multiple: pollAllowMultiple,
          p_role_names: sendToAll ? [] : roleNames,
          p_ressort_names: sendToAll ? [] : ressortNames,
          p_allow_user_options: pollAllowUserOptions,
          p_menu_url: pollMenuUrl.trim() || null,
          p_event_id: pollEventId || null,
          p_ends_at: pollEndsAtIso,
        });
        if (error) throw error;
        const map = (data ?? {}) as { feed_id?: string };
        feedId = String(map.feed_id ?? '');
      } else {
        const { data, error } = await supabase.rpc('create_feed', {
          p_text: body,
          p_image_urls: urls.join(','),
          p_role_names: sendToAll ? [] : roleNames,
          p_ressort_names: sendToAll ? [] : ressortNames,
        });
        if (error) throw error;
        feedId = String(data ?? '');
      }
      if (!feedId) throw new Error('Erstellen fehlgeschlagen: keine feed_id zurückgegeben.');

      // 3) Attachments hochladen (PDF/Audio)
      if (attachments.length) {
        if (!session?.user?.id) throw new Error('Keine Session');

        for (const f of attachments) {
          const lower = f.name.toLowerCase();
          const type =
            f.type === 'application/pdf' || lower.endsWith('.pdf')
              ? 'pdf'
              : f.type.startsWith('audio/')
                ? 'audio'
                : 'file';

          const safeName = lower.replace(/[^a-z0-9._-]+/g, '_').slice(-120);
          const aid = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : String(Date.now());
          const objectPath = `feed/${feedId}/${Date.now()}_${aid}_${safeName}`;

          const { error: upErr } = await supabase.storage.from('feed-attachments').upload(objectPath, f, {
            upsert: false,
            contentType: f.type || undefined,
            cacheControl: '3600',
          });
          if (upErr) throw upErr;

          const { error: insErr } = await supabase.from('feed_attachments').insert({
            feed_id: feedId,
            user_id: session.user.id,
            type,
            bucket_id: 'feed-attachments',
            object_path: objectPath,
            file_name: f.name,
            mime_type: f.type || null,
            size_bytes: f.size,
            duration_ms: null,
          });
          if (insErr) throw insErr;
        }
      }
    },
    onSuccess: async () => {
      const createdType = composeType;
      setText('');
      setFiles([]);
      setAttachments([]);
      setRoleNames([]);
      setRessortNames([]);
      setSendToAll(true);
      setPollQuestion('');
      setPollAllowMultiple(false);
      setPollAllowUserOptions(false);
      setPollMenuUrl('');
      setPollEventId(null);
      setPollHasDeadline(false);
      setPollEndsAt('');
      setPollOptions(['', '']);
      setComposeType('post');
      notifications.show({
        color: 'teal',
        title: 'News',
        message: createdType === 'poll' ? 'Umfrage erstellt.' : 'Meldung erstellt.',
      });
      await qc.invalidateQueries({ queryKey: ['feed'] });
    },
    onError: (e: unknown) => {
      notifications.show({
        color: 'red',
        title: 'Fehler',
        message: (e as { message?: string } | null)?.message ?? String(e),
      });
    },
  });

  const extendPollDeadlineM = useMutation({
    mutationFn: async () => {
      const pollId = pollEvalQ.data?.poll.id?.trim() ?? '';
      if (!pollId) throw new Error('Keine Umfrage ausgewählt.');
      if (!extendDeadlineValue.trim()) {
        throw new Error('Bitte eine neue Endzeit auswählen.');
      }
      const parsed = new Date(extendDeadlineValue);
      if (Number.isNaN(parsed.getTime())) {
        throw new Error('Ungültige Endzeit.');
      }
      if (parsed.getTime() <= Date.now() + 60 * 1000) {
        throw new Error('Die neue Endzeit muss in der Zukunft liegen.');
      }

      const { error } = await supabase.rpc('extend_feed_poll_deadline', {
        p_poll_id: pollId,
        p_new_ends_at: parsed.toISOString(),
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      notifications.show({
        color: 'teal',
        title: 'Umfrage',
        message: 'Endzeit wurde aktualisiert.',
      });
      await qc.invalidateQueries({ queryKey: ['poll-evaluation', pollEvalPost?.id ?? 'none'] });
      await qc.invalidateQueries({ queryKey: ['feed'] });
    },
    onError: (e: unknown) => {
      notifications.show({
        color: 'red',
        title: 'Umfrage',
        message: (e as { message?: string } | null)?.message ?? String(e),
      });
    },
  });

  const deleteM = useMutation({
    mutationFn: async (id: string) => {
      const ok = window.confirm('Meldung wirklich löschen? (inkl. Kommentare/Reaktionen)');
      if (!ok) return;
      const { error } = await supabase.from('feed').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: async () => {
      notifications.show({ color: 'teal', title: 'News', message: 'Gelöscht.' });
      await qc.invalidateQueries({ queryKey: ['feed'] });
    },
    onError: (e: unknown) => {
      notifications.show({
        color: 'red',
        title: 'Löschen fehlgeschlagen',
        message: (e as { message?: string } | null)?.message ?? String(e),
      });
    },
  });

  const addCommentM = useMutation({
    mutationFn: async () => {
      const pid = commentsPost?.id;
      const uid = session?.user?.id;
      const body = commentDraft.trim();
      if (!pid) throw new Error('Kein Post ausgewaehlt');
      if (!uid) throw new Error('Nicht eingeloggt');
      if (!body) throw new Error('Bitte Kommentar eingeben.');
      const { error } = await supabase.from('comments').insert({ post_id: pid, user_id: uid, comment_text: body });
      if (error) throw error;
    },
    onSuccess: async () => {
      setCommentDraft('');
      if (commentsPost?.id) {
        await qc.invalidateQueries({ queryKey: ['comments', commentsPost.id] });
        await qc.invalidateQueries({ queryKey: ['feed'] });
      }
      notifications.show({ color: 'teal', title: 'Kommentar', message: 'Gesendet.' });
    },
    onError: (e: unknown) => {
      notifications.show({ color: 'red', title: 'Kommentar', message: (e as { message?: string } | null)?.message ?? String(e) });
    },
  });

  const deleteCommentM = useMutation({
    mutationFn: async (commentId: string) => {
      const ok = window.confirm('Kommentar wirklich loeschen?');
      if (!ok) return;
      const { error } = await supabase.from('comments').delete().eq('id', commentId);
      if (error) throw error;
    },
    onSuccess: async () => {
      if (commentsPost?.id) {
        await qc.invalidateQueries({ queryKey: ['comments', commentsPost.id] });
        await qc.invalidateQueries({ queryKey: ['feed'] });
      }
    },
    onError: (e: unknown) => {
      notifications.show({ color: 'red', title: 'Loeschen fehlgeschlagen', message: (e as { message?: string } | null)?.message ?? String(e) });
    },
  });

  const toggleLikeM = useMutation({
    mutationFn: async (commentId: string) => {
      const uid = session?.user?.id;
      if (!uid) throw new Error('Nicht eingeloggt');

      const liked = likeInfo.mine.has(commentId);
      if (liked) {
        const { error } = await supabase.from('comment_likes').delete().eq('comment_id', commentId).eq('user_id', uid);
        if (error) throw error;
        return;
      }

      const { error } = await supabase.from('comment_likes').insert({ comment_id: commentId, user_id: uid });
      if (error) throw error;
    },
    onSuccess: async () => {
      if (commentsPost?.id) {
        await qc.invalidateQueries({ queryKey: ['comment-likes', commentsPost.id] });
        await qc.invalidateQueries({ queryKey: ['feed'] });
      }
    },
    onError: (e: unknown) => {
      notifications.show({ color: 'red', title: 'Like', message: (e as { message?: string } | null)?.message ?? String(e) });
    },
  });

  function pickImages() {
    const inp = document.createElement('input');
    inp.type = 'file';
    inp.accept = 'image/*';
    inp.multiple = true;
    inp.onchange = () => {
      const list = Array.from(inp.files ?? []);
      if (!list.length) return;
      setFiles((prev) => [...prev, ...list]);
    };
    inp.click();
  }

  function pickAttachments() {
    const inp = document.createElement('input');
    inp.type = 'file';
    inp.accept = 'application/pdf,audio/*';
    inp.multiple = true;
    inp.onchange = () => {
      const list = Array.from(inp.files ?? []);
      if (!list.length) return;
      setAttachments((prev) => [...prev, ...list]);
    };
    inp.click();
  }

  function setPollOptionAt(index: number, value: string) {
    setPollOptions((prev) => prev.map((x, i) => (i === index ? value : x)));
  }

  function addPollOption() {
    setPollOptions((prev) => {
      if (prev.length >= 10) return prev;
      return [...prev, ''];
    });
  }

  function removePollOption(index: number) {
    setPollOptions((prev) => {
      if (prev.length <= 2) return prev;
      return prev.filter((_, i) => i !== index);
    });
  }

  async function openAttachment(att: FeedAttachmentRow) {
    try {
      const { data, error } = await supabase.storage.from(att.bucket_id).createSignedUrl(att.object_path, 60 * 10);
      if (error) throw error;
      const url = data?.signedUrl;
      if (!url) throw new Error('Keine signedUrl');
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e: unknown) {
      notifications.show({
        color: 'red',
        title: 'Anhang',
        message: (e as { message?: string } | null)?.message ?? String(e),
      });
    }
  }

  const busy = createM.isPending || deleteM.isPending;
  const commentsBusy = addCommentM.isPending || deleteCommentM.isPending || toggleLikeM.isPending;

  const attachmentsByFeedId = useMemo(() => {
    const m = new Map<string, { total: number; pdf: number; audio: number; other: number }>();
    for (const a of recentAttachmentsQ.data ?? []) {
      const cur = m.get(a.feed_id) ?? { total: 0, pdf: 0, audio: 0, other: 0 };
      cur.total += 1;
      if (a.type === 'pdf') cur.pdf += 1;
      else if (a.type === 'audio') cur.audio += 1;
      else cur.other += 1;
      m.set(a.feed_id, cur);
    }
    return m;
  }, [recentAttachmentsQ.data]);

  return (
    <Stack gap="md" className="contrast-page news-page">
      <PageHeader
        title="News, Umfragen & Auswertungen"
        description="Posts und Umfragen erstellen sowie Ergebnisse live auswerten."
        loading={rolesQ.isLoading || ressortsQ.isLoading}
      />

      <Card
        className="contrast-surface"
        radius="lg"
        p="md"
        style={{
          background: 'var(--app-surface)',
          border: '1px solid var(--app-border)',
        }}
      >
        <Stack gap="sm">
          <Group gap="xs">
            <Button
              variant={composeType === 'post' ? 'filled' : 'light'}
              color="brand"
              onClick={() => setComposeType('post')}
              disabled={busy}
            >
              Post
            </Button>
            <Button
              variant={composeType === 'poll' ? 'filled' : 'light'}
              color="grape"
              onClick={() => setComposeType('poll')}
              disabled={busy}
            >
              Umfrage
            </Button>
          </Group>

          <Textarea
            label={composeType === 'poll' ? 'Begleittext (optional)' : 'Text'}
            placeholder={composeType === 'poll' ? 'Optionaler Einleitungstext zur Umfrage' : 'Was gibt es Neues?'}
            value={text}
            onChange={(e) => setText(e.currentTarget.value)}
            autosize
            minRows={4}
          />

          {composeType === 'poll' && (
            <Box
              p="md"
              style={{
                background: 'color-mix(in srgb, var(--app-surface-2) 60%, transparent)',
                border: '1px solid var(--app-border)',
                borderRadius: 10,
              }}
            >
              <Stack gap="sm">
                <Group gap="xs" align="center">
                  <IconChartBar size={16} style={{ color: 'var(--color-primary)' }} />
                  <Text size="sm" fw={700} c="var(--color-primary)">
                    Umfrage konfigurieren
                  </Text>
                </Group>

                <TextInput
                  label="Umfragefrage"
                  placeholder="Welche Option bevorzugst du?"
                  value={pollQuestion}
                  onChange={(e) => setPollQuestion(e.currentTarget.value)}
                  radius="md"
                />

                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
                  <Switch
                    label="Mehrfachauswahl"
                    description="Mehrere Optionen wählbar"
                    checked={pollAllowMultiple}
                    onChange={(e) => setPollAllowMultiple(e.currentTarget.checked)}
                  />
                  <Switch
                    label="Eigene Antworten"
                    description="Nutzer können ergänzen"
                    checked={pollAllowUserOptions}
                    onChange={(e) => setPollAllowUserOptions(e.currentTarget.checked)}
                  />
                  <Switch
                    label="Endzeit setzen"
                    description="Abstimmung schließt automatisch"
                    checked={pollHasDeadline}
                    onChange={(e) => {
                      const checked = e.currentTarget.checked;
                      setPollHasDeadline(checked);
                      if (!checked) {
                        setPollEndsAt('');
                      } else if (!pollEndsAt) {
                        setPollEndsAt(toDateTimeLocalValue(new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString()));
                      }
                    }}
                  />
                  {pollHasDeadline && (
                    <TextInput
                      label="Endzeit"
                      type="datetime-local"
                      value={pollEndsAt}
                      onChange={(e) => setPollEndsAt(e.currentTarget.value)}
                      radius="md"
                    />
                  )}
                </SimpleGrid>

                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
                  <TextInput
                    label="Link (optional)"
                    placeholder="https://..."
                    value={pollMenuUrl}
                    onChange={(e) => setPollMenuUrl(e.currentTarget.value)}
                    radius="md"
                  />
                  <Select
                    label="Termin verknüpfen (optional)"
                    placeholder={eventsQ.isLoading ? 'Lade Termine…' : 'Kein Termin'}
                    searchable
                    clearable
                    data={pollEventOptions}
                    value={pollEventId}
                    onChange={(value) => setPollEventId(value)}
                    disabled={eventsQ.isLoading}
                    nothingFoundMessage="Kein Termin"
                    radius="md"
                  />
                </SimpleGrid>

                <Divider />

                <Group justify="space-between" align="center">
                  <Text size="sm" fw={600} c="var(--color-text)">
                    Antwortoptionen
                  </Text>
                  <Badge variant="light" color={pollOptions.length >= 10 ? 'red' : 'grape'}>
                    {pollOptions.length} / 10
                  </Badge>
                </Group>

                <Stack gap={6}>
                  {pollOptions.map((opt, idx) => (
                    <Group key={`poll-opt-${idx}`} gap="xs" align="flex-end">
                      <TextInput
                        style={{ flex: 1 }}
                        placeholder={`Option ${idx + 1}`}
                        value={opt}
                        onChange={(e) => setPollOptionAt(idx, e.currentTarget.value)}
                        radius="md"
                        leftSection={
                          <Text size="xs" c="dimmed" style={{ userSelect: 'none' }}>
                            {idx + 1}.
                          </Text>
                        }
                      />
                      <ActionIcon
                        variant="light"
                        color="red"
                        disabled={pollOptions.length <= 2}
                        onClick={() => removePollOption(idx)}
                        radius="md"
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  ))}
                </Stack>

                <Group justify="space-between" align="center">
                  <Text c="dimmed" size="xs">
                    {pollAllowUserOptions
                      ? 'Nutzer können eigene Antworten ergänzen.'
                      : 'Mind. 2, max. 10 eindeutige Optionen.'}
                  </Text>
                  <Button
                    size="xs"
                    variant="light"
                    color="grape"
                    leftSection={<IconPlus size={14} />}
                    onClick={addPollOption}
                    disabled={pollOptions.length >= 10}
                    radius="md"
                  >
                    Option hinzufügen
                  </Button>
                </Group>
              </Stack>
            </Box>
          )}

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            <MultiSelect
              label="Rollen"
              data={roleOptions}
              value={sendToAll ? [] : roleNames}
              onChange={setRoleNames}
              searchable
              clearable
              disabled={sendToAll}
              nothingFoundMessage="Keine Rolle"
            />
            <MultiSelect
              label="Ressorts"
              data={ressortOptions}
              value={sendToAll ? [] : ressortNames}
              onChange={setRessortNames}
              searchable
              clearable
              disabled={sendToAll}
              nothingFoundMessage="Kein Ressort"
            />
          </SimpleGrid>

          <Group justify="space-between">
            <Group gap="xs">
              <Button
                variant={sendToAll ? 'filled' : 'light'}
                color="brand"
                onClick={() => setSendToAll(true)}
                disabled={busy}
              >
                An alle
              </Button>
              <Button
                variant={!sendToAll ? 'filled' : 'light'}
                color="gray"
                className="contrast-secondary-btn"
                onClick={() => setSendToAll(false)}
                disabled={busy}
              >
                Zielgruppe wählen
              </Button>
            </Group>

            <Group gap="xs">
              <Button leftSection={<IconPhoto size={16} />} variant="light" onClick={pickImages} disabled={busy}>
                Bilder ({files.length})
              </Button>
              <Button leftSection={<IconPaperclip size={16} />} variant="light" onClick={pickAttachments} disabled={busy}>
                Dateien ({attachments.length})
              </Button>
              <Button leftSection={<IconSend size={16} />} onClick={() => createM.mutate()} loading={createM.isPending}>
                {composeType === 'poll' ? 'Umfrage veröffentlichen' : 'Senden'}
              </Button>
            </Group>
          </Group>

          {files.length > 0 && (
            <>
              <Divider />
              <Group gap="xs">
                {files.map((f, idx) => (
                  <Badge
                    key={idx}
                    variant="light"
                    color="gray"
                    className="contrast-badge-neutral"
                    rightSection={
                      <ActionIcon
                        className="contrast-icon-btn"
                        size="xs"
                        variant="subtle"
                        color="gray"
                        onClick={() => setFiles((prev) => prev.filter((_, i) => i !== idx))}
                      >
                        <IconX size={12} />
                      </ActionIcon>
                    }
                  >
                    {f.name}
                  </Badge>
                ))}
              </Group>
            </>
          )}

          {attachments.length > 0 && (
            <>
              <Divider />
              <Group gap="xs">
                {attachments.map((f, idx) => (
                  <Badge
                    key={idx}
                    variant="light"
                    color="gray"
                    className="contrast-badge-neutral"
                    leftSection={f.type.startsWith('audio/') ? <IconMusic size={12} /> : <IconFileText size={12} />}
                    rightSection={
                      <ActionIcon
                        className="contrast-icon-btn"
                        size="xs"
                        variant="subtle"
                        color="gray"
                        onClick={() => setAttachments((prev) => prev.filter((_, i) => i !== idx))}
                      >
                        <IconX size={12} />
                      </ActionIcon>
                    }
                  >
                    {f.name}
                  </Badge>
                ))}
              </Group>
            </>
          )}
        </Stack>
      </Card>

      <Card
        className="contrast-surface"
        radius="lg"
        p="md"
        style={{
          background: 'var(--app-surface)',
          border: '1px solid var(--app-border)',
        }}
      >
        <Stack gap="sm">
          <Group justify="space-between" align="flex-end">
            <Box>
              <Text c="var(--color-text)" fw={700}>
                Meldungen & Umfragen
              </Text>
              <Text c="dimmed" size="sm">
                Standard: alles. Optional kannst du filtern, wie es für eine Rolle/Ressort aussehen würde.
              </Text>
            </Box>
            <Group gap="xs" align="center">
              {feedQ.isLoading && <Loader size="sm" />}
              {(filterRoles.length > 0 || filterRessorts.length > 0) && (
                <Button
                  variant="light"
                  color="gray"
                  className="contrast-secondary-btn"
                  onClick={() => {
                    setFilterRoles([]);
                    setFilterRessorts([]);
                  }}
                >
                  Filter reset
                </Button>
              )}
            </Group>
          </Group>

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            <MultiSelect
              label="Filter Rollen"
              data={roleOptions}
              value={filterRoles}
              onChange={(v) => {
                setFilterRoles(v);
                setShowCount(40);
              }}
              searchable
              clearable
              nothingFoundMessage="Keine Rolle"
            />
            <MultiSelect
              label="Filter Ressorts"
              data={ressortOptions}
              value={filterRessorts}
              onChange={(v) => {
                setFilterRessorts(v);
                setShowCount(40);
              }}
              searchable
              clearable
              nothingFoundMessage="Kein Ressort"
            />
          </SimpleGrid>

          {feedQ.isError ? (
            <EmptyState
              icon={<IconSpeakerphone size={48} />}
              title="Feed konnte nicht geladen werden"
              description="Bitte Seite neu laden oder Admin kontaktieren."
            />
          ) : shownFeed.length === 0 && !feedQ.isLoading ? (
            <EmptyState
              icon={<IconSpeakerphone size={48} />}
              title="Noch keine Meldungen"
              description="Erstelle oben den ersten Post oder die erste Umfrage."
            />
          ) : (
            <ScrollArea h={480}>
              <Stack gap="xs">
                {shownFeed.map((p) => {
                const urls = parseImageUrls(p.image_url);
                const att = attachmentsByFeedId.get(p.id);
                const poll = p.poll ?? null;
                const attHint = att
                  ? `Anhaenge: ${att.total} (PDF ${att.pdf}, Audio ${att.audio}${att.other ? `, Sonst ${att.other}` : ''})`
                  : 'Keine Anhaenge';
                const pollEventSummary = poll
                  ? formatEventSummary(
                      poll.event_title,
                      poll.event_type,
                      poll.event_start_time,
                      poll.event_end_time,
                    )
                  : '';
                const pollDeadlineLabel = poll ? formatPollDeadlineLabel(poll.ends_at) : '';
                const pollClosed = poll ? isPollClosed(poll.ends_at, poll.is_closed) : false;
                return (
                  <Card
                    key={p.id}
                    className="contrast-surface-soft"
                    radius="md"
                    p="sm"
                    style={{
                      background: 'var(--app-surface-3)',
                      border: '1px solid var(--app-border-2)',
                    }}
                  >
                    <Group justify="space-between" align="flex-start">
                      <Stack gap={4} style={{ flex: 1 }}>
                        <Group gap="xs">
                          <Text c="var(--color-text)" fw={700} size="sm">
                            {authorLabel(p)}
                          </Text>
                          <Text c="dimmed" size="xs">
                            {new Date(p.created_at).toLocaleString()}
                          </Text>
                        </Group>
                        {/* Nur anzeigen wenn Begleittext vorhanden (nicht wenn Text == Frage, dann wäre es doppelt) */}
                        {!(poll && p.text === poll.question) && (
                          <Text c="var(--color-text)" size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                            {p.text}
                          </Text>
                        )}
                        {poll && (
                          <Stack gap={6}>
                            <Group gap={6}>
                              <Badge variant="light" color="grape">
                                Umfrage
                              </Badge>
                              {pollClosed && (
                                <Badge variant="light" color="red">
                                  Geschlossen
                                </Badge>
                              )}
                              <Text c="dimmed" size="xs">
                                {poll.total_votes} {poll.total_votes === 1 ? 'Stimme' : 'Stimmen'}
                              </Text>
                            </Group>
                            <Text c="var(--color-text)" size="sm" fw={600}>
                              {poll.question}
                            </Text>
                            {/* Option bars */}
                            {poll.options.length > 0 && (
                              <Stack gap={4} mt={2}>
                                {poll.options
                                  .slice()
                                  .sort((a, b) => b.vote_count - a.vote_count)
                                  .map((opt, idx) => {
                                    const pct = poll.total_votes > 0
                                      ? (opt.vote_count / poll.total_votes) * 100
                                      : 0;
                                    const barColor = idx === 0 ? 'grape' : idx === 1 ? 'violet' : 'indigo';
                                    return (
                                      <Box key={opt.id}>
                                        <Group justify="space-between" gap="xs" mb={2}>
                                          <Text size="xs" c="var(--color-text)" style={{ flex: 1 }} truncate>
                                            {opt.text}
                                          </Text>
                                          <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                                            {opt.vote_count} ({pct.toFixed(0)}%)
                                          </Text>
                                        </Group>
                                        <Progress value={pct} color={barColor} size="xs" radius="xl" />
                                      </Box>
                                    );
                                  })}
                              </Stack>
                            )}
                            {pollDeadlineLabel && (
                              <Text c={pollClosed ? 'red.4' : 'dimmed'} size="xs" fw={600}>
                                {pollClosed ? `Abgelaufen: ${pollDeadlineLabel}` : `Endet: ${pollDeadlineLabel}`}
                              </Text>
                            )}
                            {pollEventSummary && (
                              <Badge variant="light" color="indigo" style={{ alignSelf: 'flex-start' }}>
                                {pollEventSummary}
                              </Badge>
                            )}
                          </Stack>
                        )}
                        {urls.length > 0 && (
                          <Text c="dimmed" size="xs">
                            {urls.length} Bild(er)
                          </Text>
                        )}
                        {renderVisibilityBadges(p)}
                      </Stack>

                      <Group gap="xs" align="center">
                        <Tooltip label="Kommentare ansehen" withArrow>
                          <Button
                            size="xs"
                            variant="subtle"
                            color="gray"
                            className="contrast-secondary-btn"
                            leftSection={<IconMessageCircle size={14} />}
                            onClick={() => {
                              setCommentsPost(p);
                              setCommentSearch('');
                              setCommentDraft('');
                              setCommentsOpen(true);
                            }}
                          >
                            {p.comment_count ?? 0}
                          </Button>
                        </Tooltip>
                        {att && att.total > 0 && (
                          <Tooltip label={attHint} withArrow>
                            <Button
                              size="xs"
                              variant="subtle"
                              color="gray"
                              className="contrast-secondary-btn"
                              leftSection={<IconPaperclip size={14} />}
                              onClick={() => {
                                setAttachmentsPost(p);
                                setAttachmentsOpen(true);
                              }}
                            >
                              {att.total}
                            </Button>
                          </Tooltip>
                        )}
                        {poll && (
                          <Tooltip label="Umfrage auswerten" withArrow>
                            <Button
                              size="xs"
                              variant="subtle"
                              color="grape"
                              leftSection={<IconChartBar size={14} />}
                              onClick={() => {
                                setPollEvalPost(p);
                                setPollEvalOpen(true);
                              }}
                            >
                              Auswertung
                            </Button>
                          </Tooltip>
                        )}
                        <Tooltip label="Löschen" withArrow>
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            onClick={() => deleteM.mutate(p.id)}
                            disabled={busy}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Group>
                  </Card>
                );
              })}
              </Stack>
            </ScrollArea>
          )}

          <Group justify="space-between" align="center" mt="sm">
            <Text c="dimmed" size="sm">
              Anzeige: {shownFeed.length} / {filteredFeed.length}
            </Text>
            {hasMoreFeed && (
              <Button variant="light" color="gray" className="contrast-secondary-btn" onClick={() => setShowCount((n) => n + 40)}>
                Mehr laden
              </Button>
            )}
          </Group>
        </Stack>
      </Card>

      <Modal
        opened={commentsOpen}
        onClose={() => setCommentsOpen(false)}
        title="Kommentare"
        centered
        size="lg"
        overlayProps={{ blur: 2 }}
      >
        {!commentsPost ? (
          <Text c="dimmed">Kein Post ausgewaehlt.</Text>
        ) : (
          <Stack gap="sm">
            <Box>
              <Text c="var(--color-text)" fw={800}>
                {authorLabel(commentsPost)}
              </Text>
              <Text c="dimmed" size="sm">
                {new Date(commentsPost.created_at).toLocaleString()}
              </Text>
              <Text c="var(--color-text)" size="sm" style={{ whiteSpace: 'pre-wrap', marginTop: 6 }}>
                {commentsPost.text}
              </Text>
            </Box>

            <Divider />

            <TextInput
              label="Suchen"
              placeholder="Name oder Text..."
              value={commentSearch}
              onChange={(e) => setCommentSearch(e.currentTarget.value)}
            />

            {(commentsQ.isLoading || commentLikesQ.isLoading || profilesQ.isLoading) ? (
              <Group gap="xs">
                <Loader size="sm" />
                <Text c="dimmed">Lade Kommentare…</Text>
              </Group>
            ) : commentsQ.isError ? (
              <Text c="dimmed">Konnte Kommentare nicht laden.</Text>
            ) : (
              (() => {
                const list = (commentsQ.data ?? []).filter((c) => {
                  if (!commentSearch.trim()) return true;
                  const q = commentSearch.toLowerCase();
                  const text = (c.comment_text ?? '').toLowerCase();
                  const author = profileLabel(profileById.get(c.user_id ?? ''));
                  return text.includes(q) || author.toLowerCase().includes(q);
                });

                return (
                  <ScrollArea h={360} offsetScrollbars>
                    <Stack gap="xs">
                      {list.length === 0 ? (
                        <Text c="dimmed">Keine Kommentare.</Text>
                      ) : (
                        list.map((c) => {
                          const author = profileById.get(c.user_id ?? '');
                          const created = c.created_at ? new Date(c.created_at).toLocaleString() : '';
                          const count = likeInfo.counts.get(c.id) ?? 0;
                          const liked = likeInfo.mine.has(c.id);
                          const canDelete = capabilities.isAdmin || (session?.user?.id && session.user.id === c.user_id);

                          return (
                            <Card
                              key={c.id}
                              className="contrast-surface-soft"
                              radius="md"
                              p="sm"
                              style={{
                                background: 'var(--app-surface-3)',
                                border: '1px solid var(--app-border-2)',
                              }}
                            >
                              <Group justify="space-between" align="flex-start">
                                <Group gap="sm" wrap="nowrap" style={{ flex: 1 }}>
                                  <Avatar src={author?.avatar_url ?? undefined} radius="xl" />
                                  <Box style={{ flex: 1 }}>
                                    <Group gap="xs">
                                      <Text c="var(--color-text)" fw={800} size="sm">
                                        {profileLabel(author)}
                                      </Text>
                                      {created && (
                                        <Text c="dimmed" size="xs">
                                          {created}
                                        </Text>
                                      )}
                                    </Group>
                                    <Text c="var(--color-text)" size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                                      {c.comment_text}
                                    </Text>
                                  </Box>
                                </Group>

                                <Group gap="xs" align="center">
                                  <Tooltip label={liked ? 'Like entfernen' : 'Liken'} withArrow>
                                    <Button
                                      size="xs"
                                      variant={liked ? 'light' : 'subtle'}
                                      color={liked ? 'red' : 'gray'}
                                      className={liked ? undefined : 'contrast-secondary-btn'}
                                      leftSection={<IconHeart size={14} />}
                                      onPointerDown={() => startLongPress(c.id)}
                                      onPointerUp={() => handleLikeRelease(c.id)}
                                      onPointerLeave={() => cancelLongPress()}
                                      onTouchStart={() => startLongPress(c.id)}
                                      onTouchEnd={() => handleLikeRelease(c.id)}
                                      disabled={!session?.user?.id || commentsBusy}
                                    >
                                      {count}
                                    </Button>
                                  </Tooltip>
                                  {canDelete && (
                                    <Tooltip label="Kommentar loeschen" withArrow>
                                      <ActionIcon
                                        variant="subtle"
                                        color="red"
                                        onClick={() => deleteCommentM.mutate(c.id)}
                                        disabled={commentsBusy}
                                      >
                                        <IconTrash size={16} />
                                      </ActionIcon>
                                    </Tooltip>
                                  )}
                                </Group>
                              </Group>
                            </Card>
                          );
                        })
                      )}
                    </Stack>
                  </ScrollArea>
                );
              })()
            )}

            <Divider />

            <Textarea
              label="Antworten"
              placeholder="Kommentar schreiben..."
              value={commentDraft}
              onChange={(e) => setCommentDraft(e.currentTarget.value)}
              autosize
              minRows={3}
            />

            <Group justify="flex-end">
              <Button
                leftSection={<IconSend size={16} />}
                onClick={() => addCommentM.mutate()}
                loading={addCommentM.isPending}
                disabled={!commentDraft.trim() || !session?.user?.id || commentsBusy}
              >
                Senden
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      <Modal
        opened={likesModalOpen}
        onClose={() => setLikesModalOpen(false)}
        title="Gefällt mir"
        centered
        size="sm"
        overlayProps={{ blur: 2 }}
      >
        {likesModalLoading ? (
          <Group gap="xs">
            <Loader size="sm" />
            <Text c="dimmed">Lade Liste…</Text>
          </Group>
        ) : !likesModalProfiles || likesModalProfiles.length === 0 ? (
          <Text c="dimmed">Noch keine Likes.</Text>
        ) : (
          <Stack gap="xs">
            {likesModalProfiles.map((p) => (
              <Group key={p.id} gap="sm" align="center">
                <Avatar src={p.avatar_url ?? undefined} radius="xl" />
                <Text c="var(--color-text)">{profileLabel(p)}</Text>
              </Group>
            ))}
          </Stack>
        )}
      </Modal>

      <Modal
        opened={pollEvalOpen}
        onClose={() => setPollEvalOpen(false)}
        title="Umfrage-Auswertung"
        centered
        size="lg"
        overlayProps={{ blur: 2 }}
      >
        {!pollEvalPost ? (
          <Text c="dimmed">Kein Post ausgewaehlt.</Text>
        ) : pollEvalQ.isLoading ? (
          <Group gap="xs">
            <Loader size="sm" />
            <Text c="dimmed">Lade Auswertung…</Text>
          </Group>
        ) : pollEvalQ.isError ? (
          <Text c="dimmed">Konnte Auswertung nicht laden.</Text>
        ) : !pollEvalQ.data ? (
          <Text c="dimmed">Dieser Beitrag enthält keine Umfrage.</Text>
        ) : (
          (() => {
            const pollDeadlineLabel = formatPollDeadlineLabel(pollEvalQ.data.poll.ends_at);
            const pollClosed = isPollClosed(
              pollEvalQ.data.poll.ends_at,
              pollEvalQ.data.poll.is_closed,
            );
            return (
          <Stack gap="sm">
            <Box>
              <Text c="var(--color-text)" fw={800}>
                {authorLabel(pollEvalPost)}
              </Text>
              <Text c="dimmed" size="sm">
                {new Date(pollEvalPost.created_at).toLocaleString()}
              </Text>
              <Text c="var(--color-text)" size="sm" style={{ whiteSpace: 'pre-wrap', marginTop: 6 }}>
                {pollEvalPost.text}
              </Text>
            </Box>

            <Divider />

            <Card
              className="contrast-surface-soft"
              radius="md"
              p="sm"
              style={{
                background: 'var(--app-surface-3)',
                border: '1px solid var(--app-border-2)',
              }}
            >
              <Stack gap="xs">
                <Group justify="space-between" align="center">
                  <Text c="var(--color-text)" fw={700}>
                    {pollEvalQ.data.poll.question}
                  </Text>
                  <Badge variant="light" color={pollEvalQ.data.poll.allow_multiple ? 'grape' : 'brand'}>
                    {pollEvalQ.data.poll.allow_multiple ? 'Mehrfachauswahl' : 'Einzelauswahl'}
                  </Badge>
                </Group>
                <Text c="dimmed" size="sm">
                  Stimmen: {pollEvalQ.data.poll.total_votes} · Teilnehmende: {pollEvalQ.data.participantCount}
                </Text>
                {pollDeadlineLabel && (
                  <Text c={pollClosed ? 'red.4' : 'dimmed'} size="sm" fw={600}>
                    {pollClosed ? `Abgelaufen: ${pollDeadlineLabel}` : `Endet: ${pollDeadlineLabel}`}
                  </Text>
                )}
                {pollParticipationSummary && (
                  <Text c="dimmed" size="sm">
                    Zielgruppe: {pollEvalQ.data.eligibleVoterCount > 0 ? pollEvalQ.data.eligibleVoterCount : pollEvalQ.data.participantCount} · Teilnahme: {pollParticipationSummary}
                  </Text>
                )}
                {pollEvalEventSummary && (
                  <Badge variant="light" color="indigo" style={{ alignSelf: 'flex-start' }}>
                    <Group gap={4} wrap="nowrap">
                      <IconCalendarEvent size={12} />
                      <Text size="xs">{pollEvalEventSummary}</Text>
                    </Group>
                  </Badge>
                )}
                {myPollSelectionText ? (
                  <Badge variant="light" color="teal" style={{ alignSelf: 'flex-start' }}>
                    Du hast abgestimmt: {myPollSelectionText}
                  </Badge>
                ) : (
                  <Badge variant="light" color="gray" style={{ alignSelf: 'flex-start' }}>
                    Du hast noch nicht abgestimmt
                  </Badge>
                )}
                <Group gap="xs" wrap="wrap" className="no-print">
                  <Button
                    size="xs"
                    variant="light"
                    color="teal"
                    leftSection={<IconCopy size={14} />}
                    onClick={() => void copyPollOrderSummary()}
                  >
                    Kopieren
                  </Button>
                  <Button
                    size="xs"
                    variant="light"
                    color="cyan"
                    leftSection={<IconFileText size={14} />}
                    onClick={() => {
                      if (pollEvalQ.data) exportPollCsv(pollEvalQ.data);
                    }}
                  >
                    CSV / Excel
                  </Button>
                  <Button
                    size="xs"
                    variant="light"
                    color="gray"
                    leftSection={<IconPrinter size={14} />}
                    onClick={() => window.print()}
                  >
                    PDF drucken
                  </Button>
                </Group>
                <Group gap="xs" wrap="wrap">
                  <TextInput
                    label="Endzeit anpassen"
                    type="datetime-local"
                    value={extendDeadlineValue}
                    onChange={(e) => setExtendDeadlineValue(e.currentTarget.value)}
                    style={{ minWidth: 240 }}
                  />
                  <Button
                    size="xs"
                    variant="light"
                    color="grape"
                    loading={extendPollDeadlineM.isPending}
                    onClick={() => extendPollDeadlineM.mutate()}
                    disabled={!extendDeadlineValue.trim()}
                    style={{ marginTop: 22 }}
                  >
                    Endzeit verlängern
                  </Button>
                </Group>
                {pollEvalQ.data.poll.menu_url && (
                  <Button
                    component="a"
                    href={pollEvalQ.data.poll.menu_url}
                    target="_blank"
                    rel="noreferrer"
                    size="xs"
                    variant="light"
                    color="grape"
                    leftSection={<IconExternalLink size={14} />}
                    style={{ alignSelf: 'flex-start' }}
                  >
                    Speisekarte / Link öffnen
                  </Button>
                )}
              </Stack>
            </Card>

            <ScrollArea h={360} offsetScrollbars>
              <Stack gap="xs">
                <Text c="var(--color-text)" fw={700} size="sm">
                  Ergebnisübersicht
                </Text>
                {pollOrderRows.length === 0 ? (
                  <Text c="dimmed" size="sm">
                    Noch keine Stimmen vorhanden.
                  </Text>
                ) : (
                  pollOrderRows.map((row) => (
                    <Card
                      key={`order-${row.optionId}`}
                      className="contrast-surface-soft"
                      radius="md"
                      p="sm"
                      style={{
                        background: 'var(--app-surface-3)',
                        border: '1px solid var(--app-border-2)',
                      }}
                    >
                      <Stack gap={6}>
                        <Group justify="space-between" gap="xs">
                          <Text c="var(--color-text)" size="sm" fw={700}>
                            {row.optionText}
                          </Text>
                          <Badge variant="light" color="grape">
                            {row.count}
                          </Badge>
                        </Group>
                        {row.voters.length === 0 ? (
                          <Text c="dimmed" size="xs">
                            Noch niemand
                          </Text>
                        ) : (
                          <Group gap={6}>
                            {row.voters.map((voter) => (
                              <Badge key={`${row.optionId}-${voter}`} variant="light" color="gray">
                                {voter}
                              </Badge>
                            ))}
                          </Group>
                        )}
                      </Stack>
                    </Card>
                  ))
                )}

                <Divider my={4} />
                <Text c="var(--color-text)" fw={700} size="sm">
                  Optionen
                </Text>
                {pollEvalQ.data.poll.options.map((opt) => {
                  const totalVotes = pollEvalQ.data?.poll.total_votes ?? 0;
                  const pct = totalVotes > 0 ? (opt.vote_count / totalVotes) * 100 : 0;
                  return (
                    <Card
                      key={opt.id}
                      className="contrast-surface-soft"
                      radius="md"
                      p="sm"
                      style={{
                        background: 'var(--app-surface-3)',
                        border: '1px solid var(--app-border-2)',
                      }}
                    >
                      <Stack gap={6}>
                        <Group justify="space-between" gap="xs">
                          <Group gap={6}>
                            <Text c="var(--color-text)" size="sm" fw={700}>
                              {opt.text}
                            </Text>
                            {opt.is_user_generated && (
                              <Badge variant="light" color="gray">
                                Community
                              </Badge>
                            )}
                          </Group>
                          <Text c="dimmed" size="xs">
                            {opt.vote_count} ({pct.toFixed(1)}%)
                          </Text>
                        </Group>
                        <Progress value={pct} color="grape" />
                        {opt.voter_names && opt.voter_names.length > 0 && (
                          <Text c="dimmed" size="xs">
                            {opt.voter_names.join(', ')}
                          </Text>
                        )}
                      </Stack>
                    </Card>
                  );
                })}

                <Divider my={4} />
                <Text c="var(--color-text)" fw={700} size="sm">
                  Wer hat was gewählt
                </Text>
                {pollEvalQ.data.participants.length === 0 ? (
                  <Text c="dimmed" size="sm">
                    Noch keine Stimmen vorhanden.
                  </Text>
                ) : (
                  [...pollEvalQ.data.participants]
                    .sort((a, b) => a.display_name.localeCompare(b.display_name, 'de', { sensitivity: 'base' }))
                    .map((entry) => (
                    <Card
                      key={entry.user_id}
                      className="contrast-surface-soft"
                      radius="md"
                      p="sm"
                      style={{
                        background: 'var(--app-surface-3)',
                        border: '1px solid var(--app-border-2)',
                      }}
                    >
                      <Stack gap={6}>
                        <Group gap="sm" align="center">
                          <Avatar src={entry.avatar_url ?? undefined} radius="xl" />
                          <Stack gap={0} style={{ flex: 1 }}>
                            <Text c="var(--color-text)" size="sm" fw={700}>
                              {entry.display_name}
                            </Text>
                            {entry.voted_at && (
                              <Text c="dimmed" size="xs">
                                {new Date(entry.voted_at).toLocaleString()}
                              </Text>
                            )}
                          </Stack>
                        </Group>
                        <Group gap={6}>
                          {entry.selected_option_texts.map((optionText, idx) => (
                            <Badge key={`${entry.user_id}-${idx}`} variant="light" color="grape">
                              {optionText}
                            </Badge>
                          ))}
                        </Group>
                      </Stack>
                    </Card>
                    ))
                )}
              </Stack>
            </ScrollArea>
          </Stack>
            );
          })()
        )}
      </Modal>

      <Modal
        opened={attachmentsOpen}
        onClose={() => setAttachmentsOpen(false)}
        title="Anhänge"
        centered
        size="lg"
        overlayProps={{ blur: 2 }}
      >
        {!attachmentsPost ? (
          <Text c="dimmed">Kein Post ausgewaehlt.</Text>
        ) : (
          <Stack gap="sm">
            <Box>
              <Text c="var(--color-text)" fw={800}>
                {authorLabel(attachmentsPost)}
              </Text>
              <Text c="dimmed" size="sm">
                {new Date(attachmentsPost.created_at).toLocaleString()}
              </Text>
              <Text c="var(--color-text)" size="sm" style={{ whiteSpace: 'pre-wrap', marginTop: 6 }}>
                {attachmentsPost.text}
              </Text>
            </Box>

            <Divider />

            {postAttachmentsQ.isLoading ? (
              <Group gap="xs">
                <Loader size="sm" />
                <Text c="dimmed">Lade Anhänge…</Text>
              </Group>
            ) : postAttachmentsQ.isError ? (
              <Text c="dimmed">Konnte Anhänge nicht laden.</Text>
            ) : (
              <ScrollArea h={360} offsetScrollbars>
                <Stack gap="xs">
                  {(postAttachmentsQ.data ?? []).length === 0 ? (
                    <Text c="dimmed">Keine Anhänge.</Text>
                  ) : (
                    (postAttachmentsQ.data ?? []).map((a) => {
                      const icon =
                        a.type === 'pdf' ? <IconFileText size={16} /> : a.type === 'audio' ? <IconMusic size={16} /> : <IconPaperclip size={16} />;
                      const meta = [a.mime_type ?? '', formatBytes(a.size_bytes)].filter(Boolean).join(' · ');
                      return (
                        <Card
                          key={a.id}
                          className="contrast-surface-soft"
                          radius="md"
                          p="sm"
                          style={{
                            background: 'var(--app-surface-3)',
                            border: '1px solid var(--app-border-2)',
                          }}
                        >
                          <Group justify="space-between" align="flex-start">
                            <Group gap="sm" wrap="nowrap" style={{ flex: 1 }}>
                              <Avatar radius="md" color="dark">
                                {icon}
                              </Avatar>
                              <Box style={{ flex: 1 }}>
                                <Text c="var(--color-text)" fw={700} size="sm" lineClamp={1}>
                                  {a.file_name ?? a.object_path}
                                </Text>
                                {meta && (
                                  <Text c="dimmed" size="xs">
                                    {meta}
                                  </Text>
                                )}
                                <Group gap="xs" mt={6}>
                                  <Badge variant="light" color={a.type === 'audio' ? 'grape' : a.type === 'pdf' ? 'cyan' : 'gray'}>
                                    {a.type}
                                  </Badge>
                                </Group>
                              </Box>
                            </Group>

                            <Group gap="xs">
                              <Button size="xs" variant="light" leftSection={<IconExternalLink size={14} />} onClick={() => void openAttachment(a)}>
                                Oeffnen
                              </Button>
                            </Group>
                          </Group>
                        </Card>
                      );
                    })
                  )}
                </Stack>
              </ScrollArea>
            )}
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}
